import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  useContext,
  useStore,
  useRoute,
  onMounted,
  useRouter,
  ref,
} from '@nuxtjs/composition-api';
import type { Route } from 'vue-router';
import GoogleButton from './auth/GoogleButton.vue';
import FacebookButton from './auth/FacebookButton.vue';
import TDivider from '~/components/elements/t-divider.vue';
import countries from '~/data/countries';
import LoginError from '~/components/auth/LoginError.vue';
import TButton from '~/components/atoms/t-button.vue';
import TLabel from '~/components/atoms/t-label.vue';
import TInput from '~/components/atoms/t-input.vue';
import TInputGroup from '~/components/elements/t-input-group.vue';
import { State } from '@/store';
import { getFirstItemFromQueryParam } from '~/utils/route';
import { LoginType } from '~/types/api';
import validateEmail from '~/utils/validateEmail';

interface Props {
  currentPageUrl?: string;
}

interface LoginErrorInterface {
  type: string;
  code?: number | string;
  state?: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'LoginForm',
  props: {
    currentPageUrl: null
  },
  emits: ['close', 'logged-in'],
  setup(__props: any, { emit: $emit }) {

/* LoginForm
 * Logs the user in via the Nuxt Auth plugin
 *
 * @parent:
 * @requests: /v1/login
 * @events:
 * @props: String currentPageUrl
 * @components:
 * @methods: login, loginFacebook, loginGoogle
 */
const $store = useStore<State>();
const $route = useRoute();
const $router = useRouter();

const {
  localePath,
  $auth,
  $config,
  $intercom,
  $intercomIdentify,
  $setAlgoliaUserToken,
  $isFlagOn,
  $addProfitwellScript,
  $setGa4UserProperties,
} = useContext();



const password = ref<string>('');
const isLoggingIn = ref<boolean>(false); // Loading state
const email = ref<string>('');
const emailValidationMessage = ref<string>('');

if (!validateEmail(email.value)) {
  emailValidationMessage.value = 'Please enter a valid email address';
}

const error = ref<LoginErrorInterface | null>(null);



const redirectTo = computed<string>((): string => $store.getters.redirectTo);
const isAuthenticated = computed(() => $store.getters.isAuthenticated);
const deviceId = computed(() => $store.getters.deviceId);
const redirectToBilling = computed<string>((): string => $store.getters.redirectToBilling);

const sanitisedRedirect = computed<string>((): string => {
  if (String(redirectTo.value).includes('v1.teachstarter.com')) {
    return redirectTo.value;
  }
  if ($route.value.query.redirect) {
    return getFirstItemFromQueryParam($route.value.query.redirect);
  }
  if ($route.value.query?.state) {
    const socialSignupState = new URLSearchParams(
      decodeURIComponent($route.value.query.state as string)
    );
    return Object.fromEntries(socialSignupState.entries()).redirectTo;
  }
  if ($route.value.path.includes('/teaching-resource/')) {
    return $route.value.path;
  }
  if (redirectToBilling.value.includes('member-settings/payment-method')) {
    return localePath(redirectToBilling.value);
  }

  return localePath(redirectTo.value);
});

onMounted(() => {
  // Show error message if in URL eg. ?error=Missing username or password
  if ($route.value.query.error) {
    // This comes from v1 when Google auth fails
    error.value = {
      type: $route.value.query.error as string,
    };
  }

  // Trigger social login on response from Facebook or Google
  if ($route.value.query.code) {
    loginSocial($route.value.query);
  }
});

const login = async () => {
  if (email.value && password.value) {
    isLoggingIn.value = true;

    try {
      const response = await $auth.loginWith('local', {
        data: {
          username: email.value,
          password: password.value,
        },
      });

      $emit('logged-in', true);
      $emit('close');

      loginSuccess(response?.data, 'email');
    } catch (err: any) {
      // Only set this to false on error because otherwise the user can click
      // the login button multiple times before the redirect has loaded. See PF-203 for details.
      isLoggingIn.value = false;
      error.value = {
        type: err.response ? 'default' : 'network',
        code: err.response?.status,
      };
      $emit('logged-in', false);
    }
  } else {
    error.value = {
      type: 'missing_data',
    };
  }
};

const loginSocial = async (query: Route['query']) => {
  if (isAuthenticated.value) {
    return false;
  }

  const params: LoginErrorInterface = {
    code:
      typeof query.code === 'string'
        ? query.code
        : typeof query.code === 'number'
          ? query.code
          : 'no code',
    type: query.scope && query.scope.includes('google') ? 'google' : 'facebook',
  };

  if (query.state) {
    params.state = query.state as string;
  }

  try {
    const response = await $auth.loginWith('local', { data: params });
    loginSuccess(response?.data, params.type as LoginType);
  } catch (err: any) {
    error.value = {
      type: params.type,
      code: err.response?.status,
    };

    $router.replace({ query: {} }, () => {});
  } finally {
    isLoggingIn.value = false;
  }
};
const loginSuccess = async (response: any, type: LoginType = 'email') => {
  await $auth.setUser(response.user);

  try {
    $addProfitwellScript(response.user);
    await $store.dispatch('resetExperiments');
    $setAlgoliaUserToken(String(response.user.id));
    $setGa4UserProperties();
  } catch (err) {
    console.error(err);
  }
  const location = response.user.location;
  if (location.code) {
    await $store.dispatch('updateLocationAccount', location);
  }

  error.value = null;

  // Set a welcome banner in the header
  $store.dispatch('setToast', {
    content: 'Welcome back ' + response.user.first_name + '!',
    image: response.user.photo,
    icon: 'stars',
  });

  // Login to WordPress too
  try {
    await $store.dispatch('loginWP');
  } catch (err) {
    // Silent fail
    console.log('WP login error', err);
  }

  eventLoggedIn(type, response.user);
  $router.push(sanitisedRedirect.value);
};

const eventLoggedIn = (type: LoginType, user: any) => {
  if ($intercom) {
    $intercom.trackEvent('Logged In');
    $intercomIdentify();
  }
};

return { __sfc: true,$store, $route, $router, localePath, $auth, $config, $intercom, $intercomIdentify, $setAlgoliaUserToken, $isFlagOn, $addProfitwellScript, $setGa4UserProperties, password, isLoggingIn, email, emailValidationMessage, error, $emit, redirectTo, isAuthenticated, deviceId, redirectToBilling, sanitisedRedirect, login, loginSocial, loginSuccess, eventLoggedIn, GoogleButton, FacebookButton, TDivider, LoginError, TButton, TLabel, TInput, TInputGroup }
}

})